/**
 * Datele demo, aceleași ca în `seed()` din demo-ul vanilla.
 * Se folosesc la `npm run db:seed` și la butonul „Resetează datele demo”.
 * Datele sunt relative la momentul rulării, ca demonstrația să pară mereu proaspătă.
 */

export interface DemoContact {
  name: string;
  email: string;
  phone: string;
  /** pe ce gamă se ocupă, la distribuitorii cu mai mulți oameni */
  role?: string;
}

export interface DemoSupplier {
  name: string;
  categories: string[];
  contacts: DemoContact[];
}

export interface DemoItem {
  /** cheie folosită doar în seed, ca să legăm prețurile de poziții */
  key: string;
  desc: string;
  qty: number;
  unit: string;
}

export interface DemoOffer {
  /** preț unitar pe cheia poziției */
  prices: Record<string, number>;
  delivery: string;
  payment: string;
  validity: string;
  notes: string;
  receivedDaysAgo: number;
}

export interface DemoSend {
  /** numele furnizorului din lista de mai sus */
  supplier: string;
  sentDaysAgo: number;
  followUps: number;
  lastFollowUpDaysAgo?: number;
  offer?: DemoOffer;
}

export interface DemoSite {
  name: string;
  locality: string;
  address: string;
  client: string;
}

export interface DemoRfq {
  title: string;
  category: string;
  /** numele șantierului din lista `sites` */
  site: string;
  /** termenul de răspuns, în zile de la momentul seed-ului */
  deadlineInDays: number;
  createdDaysAgo: number;
  note: string;
  items: DemoItem[];
  sends: DemoSend[];
}

export interface DemoData {
  company: { name: string; signature: string };
  sites: DemoSite[];
  suppliers: DemoSupplier[];
  rfqs: DemoRfq[];
}

const HVAC_ITEMS: DemoItem[] = [
  { key: 'i1', desc: 'Unitate exterioară VRF 28 kW', qty: 2, unit: 'buc' },
  { key: 'i2', desc: 'Unitate interioară tip casetă 4,5 kW', qty: 12, unit: 'buc' },
  { key: 'i3', desc: 'Țeavă cupru izolată 3/8"–5/8"', qty: 180, unit: 'm' },
  { key: 'i4', desc: 'Controler central cu ecran tactil', qty: 1, unit: 'buc' },
];

const REBAR_ITEMS: DemoItem[] = [
  { key: 'a1', desc: 'Oțel beton PC52 Ø12', qty: 8.5, unit: 't' },
  { key: 'a2', desc: 'Oțel beton PC52 Ø16', qty: 6, unit: 't' },
  { key: 'a3', desc: 'Plasă sudată Ø6 100×100', qty: 120, unit: 'buc' },
];

const hvacOffer = (
  prices: [number, number, number, number],
  delivery: string,
  payment: string,
  validity: string,
  notes: string,
  receivedDaysAgo: number,
): DemoOffer => ({
  prices: { i1: prices[0], i2: prices[1], i3: prices[2], i4: prices[3] },
  delivery,
  payment,
  validity,
  notes,
  receivedDaysAgo,
});

export const DEMO: DemoData = {
  company: {
    name: 'Construct Demo SRL',
    signature: 'Alex\nDepartament achiziții\nConstruct Demo SRL\n0700 000 000',
  },
  sites: [
    {
      name: 'Bloc P+4, Str. Lungă',
      locality: 'Brașov',
      address: 'Str. Lungă nr. 112, Brașov',
      client: 'Lungă Rezidențial SRL',
    },
    {
      name: 'Hală Cristian',
      locality: 'Cristian, BV',
      address: 'DJ 112, Cristian',
      client: 'Agrotrans Invest SRL',
    },
  ],
  suppliers: [
    {
      name: 'Climatech Instal SRL',
      categories: ['HVAC'],
      contacts: [
        { name: 'Mihai Popescu', email: 'ofertare@climatech-demo.ro', phone: '0722 100 201' },
        {
          name: 'Delia Rusu',
          email: 'delia.rusu@climatech-demo.ro',
          phone: '0722 100 205',
          role: 'Punere în funcțiune',
        },
      ],
    },
    {
      name: 'Aerotherm Distribuție',
      categories: ['HVAC'],
      contacts: [
        { name: 'Ioana Radu', email: 'vanzari@aerotherm-demo.ro', phone: '0733 410 552' },
      ],
    },
    {
      name: 'Termovent Solutions',
      categories: ['HVAC'],
      contacts: [
        { name: 'Andrei Stan', email: 'andrei.stan@termovent-demo.ro', phone: '0744 218 930' },
      ],
    },
    {
      name: 'Frigoclima Center',
      categories: ['HVAC'],
      contacts: [
        { name: 'Cristina Marin', email: 'office@frigoclima-demo.ro', phone: '0755 902 114' },
      ],
    },
    {
      name: 'Polar Instal Grup',
      categories: ['HVAC'],
      contacts: [
        { name: 'Radu Neagu', email: 'radu@polarinstal-demo.ro', phone: '0766 300 478' },
      ],
    },
    {
      name: 'Metal Depot Carpați',
      categories: ['Armătură'],
      contacts: [
        { name: 'Vlad Dinu', email: 'comenzi@metaldepot-demo.ro', phone: '0721 555 019' },
      ],
    },
    {
      name: 'Fier Beton Centru',
      categories: ['Armătură'],
      contacts: [
        { name: 'Elena Voicu', email: 'elena@fierbeton-demo.ro', phone: '0731 882 640' },
      ],
    },
    {
      name: 'Armasid Trading',
      categories: ['Armătură'],
      contacts: [
        { name: 'George Ilie', email: 'oferte@armasid-demo.ro', phone: '0742 671 305' },
      ],
    },
    {
      name: 'Cofrex Sistem',
      categories: ['Cofraje'],
      contacts: [
        { name: 'Dan Toma', email: 'dan.toma@cofrex-demo.ro', phone: '0752 144 876' },
      ],
    },
    {
      name: 'Panou Cofraj România',
      categories: ['Cofraje'],
      contacts: [
        { name: 'Ana Moldovan', email: 'ana@panoucofraj-demo.ro', phone: '0762 019 332' },
      ],
    },
    {
      name: 'Hidro Instal Distribuție',
      categories: ['Instalații sanitare', 'HVAC'],
      contacts: [
        { name: 'Sorin Pavel', email: 'sorin@hidroinstal-demo.ro', phone: '0723 740 118' },
      ],
    },
    {
      name: 'Electro Grup Montaj',
      categories: ['Electrice'],
      contacts: [
        { name: 'Bianca Ene', email: 'bianca@electrogrup-demo.ro', phone: '0734 603 925' },
      ],
    },
    // Cazul distribuitorului care vinde tot, cu un om pe fiecare gamă.
    {
      name: 'Multidepozit Construct',
      categories: ['Zidărie', 'Finisaje', 'Instalații sanitare', 'HVAC', 'Izolații'],
      contacts: [
        {
          name: 'Camelia Dobre',
          email: 'ofertare@multidepozit-demo.ro',
          phone: '0745 200 100',
          role: 'Ofertare, toate gamele',
        },
        {
          name: 'Paul Mureșan',
          email: 'paul.muresan@multidepozit-demo.ro',
          phone: '0745 200 118',
          role: 'HVAC și instalații',
        },
        {
          name: 'Raluca Barbu',
          email: 'raluca.barbu@multidepozit-demo.ro',
          phone: '0745 200 133',
          role: 'Finisaje și zidărie',
        },
      ],
    },
  ],
  rfqs: [
    {
      title: 'Echipamente HVAC – bloc P+4, Str. Lungă',
      category: 'HVAC',
      site: 'Bloc P+4, Str. Lungă',
      deadlineInDays: 4,
      createdDaysAgo: 8,
      note: 'Echipamente echivalente acceptate, cu fișă tehnică atașată.',
      items: HVAC_ITEMS,
      sends: [
        {
          supplier: 'Climatech Instal SRL',
          sentDaysAgo: 8,
          followUps: 0,
          offer: hvacOffer(
            [41200, 4850, 62, 3900],
            '14 zile',
            '30 zile de la livrare',
            '15 zile',
            'Transport inclus în Brașov.',
            5,
          ),
        },
        {
          supplier: 'Aerotherm Distribuție',
          sentDaysAgo: 8,
          followUps: 0,
          offer: hvacOffer(
            [39800, 5120, 58, 4200],
            '21 zile',
            '50% avans, 50% la livrare',
            '30 zile',
            '',
            4,
          ),
        },
        {
          supplier: 'Termovent Solutions',
          sentDaysAgo: 8,
          followUps: 1,
          offer: hvacOffer(
            [42500, 4690, 66, 3650],
            '10 zile',
            '45 zile de la livrare',
            '15 zile',
            'Punere în funcțiune inclusă.',
            2,
          ),
        },
        {
          supplier: 'Frigoclima Center',
          sentDaysAgo: 8,
          followUps: 1,
          lastFollowUpDaysAgo: 3,
        },
        { supplier: 'Polar Instal Grup', sentDaysAgo: 8, followUps: 0 },
      ],
    },
    {
      title: 'Armătură fundații – hală Cristian',
      category: 'Armătură',
      site: 'Hală Cristian',
      deadlineInDays: 6,
      createdDaysAgo: 2,
      note: 'Livrare în două tranșe.',
      items: REBAR_ITEMS,
      sends: [
        {
          supplier: 'Metal Depot Carpați',
          sentDaysAgo: 2,
          followUps: 0,
          offer: {
            prices: { a1: 3950, a2: 3920, a3: 185 },
            delivery: '5 zile',
            payment: 'la livrare',
            validity: '7 zile',
            notes: 'Preț valabil în funcție de cotația oțelului.',
            receivedDaysAgo: 1,
          },
        },
        { supplier: 'Fier Beton Centru', sentDaysAgo: 2, followUps: 0 },
        { supplier: 'Armasid Trading', sentDaysAgo: 2, followUps: 0 },
      ],
    },
  ],
};
